import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';

// ============================================
// CHECKBOX
// ============================================

export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-4 w-4 shrink-0 rounded-sm border border-[var(--border-primary)]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 data-[state=checked]:text-white',
      'data-[state=indeterminate]:bg-brand-600 data-[state=indeterminate]:border-brand-600 data-[state=indeterminate]:text-white',
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      {props.checked === 'indeterminate' ? (
        <Minus className="h-3 w-3" />
      ) : (
        <Check className="h-3 w-3" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

// ============================================
// CHECKBOX WITH LABEL
// ============================================

interface CheckboxWithLabelProps extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label: string;
  description?: string;
}

export function CheckboxWithLabel({
  label,
  description,
  className,
  id,
  ...props
}: CheckboxWithLabelProps) {
  const checkboxId = id ?? label.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className={cn('flex items-start space-x-3', className)}>
      <Checkbox id={checkboxId} {...props} />
      <div className="flex flex-col">
        <label
          htmlFor={checkboxId}
          className={cn(
            'text-sm font-medium leading-none cursor-pointer',
            'peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
          )}
        >
          {label}
        </label>
        {description && <p className="text-sm text-[var(--text-tertiary)] mt-1">{description}</p>}
      </div>
    </div>
  );
}

// ============================================
// TASK CHECKBOX (for task items)
// ============================================

interface TaskCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function TaskCheckbox({ checked, onChange, disabled, className }: TaskCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors',
        checked
          ? 'bg-green-500 border-green-500 text-white'
          : 'border-[var(--border-primary)] hover:border-[var(--border-secondary)]',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {checked && <Check className="h-3 w-3" />}
    </button>
  );
}
